import { friendlyError } from "../../../lib/friendlyError";
import { trpc } from "../../../trpc";
import { TestThenSend } from "../TestThenSend";
import { emailTypeLabel } from "../emailTypes";
import {
  EMAIL_OFF_SENTENCE,
  people,
  SEND_OFFLINE_SENTENCE,
  sendAllSentence,
  testSentSentence,
} from "../sendCopy";
import { BODY_TEXT } from "../ui";
import { useSendFlow } from "../useSendFlow";

/**
 * The thank-you after the September 2026 visit, to everyone who registered
 * for any of its events in Australia or New Zealand, whether they came or
 * not. Australians get the Australian version; Christchurch registrants
 * get the New Zealand one. A test sends you both.
 */
export function VisitThankYouPanel({
  defaultTestEmail,
  senderName,
}: {
  defaultTestEmail: string;
  senderName: string | null;
}) {
  const flow = useSendFlow(defaultTestEmail);
  const audience = trpc.adminVisitThankYouAudienceCount.useQuery(undefined, {
    retry: false,
    staleTime: 60_000,
  });
  const utils = trpc.useUtils();
  const thankYou = trpc.adminSendVisitThankYou.useMutation();

  const send = (mode: "test" | "all", testEmails: string[]) => {
    thankYou.mutate(
      {
        mode,
        testEmails: mode === "test" ? testEmails : undefined,
        testName: mode === "test" ? (senderName ?? undefined) : undefined,
      },
      {
        onSuccess: (res) => {
          if (res.mode === "test") {
            flow.testSent(
              res.skipped ? EMAIL_OFF_SENTENCE : testSentSentence(res.sentTo),
            );
          } else {
            flow.sendAllDone(
              sendAllSentence(
                res,
                "There's no one to send this to, so nothing was sent.",
              ),
            );
            void utils.adminListEmailSends.invalidate();
          }
        },
        onError: (err) =>
          flow.problem(friendlyError(err, SEND_OFFLINE_SENTENCE)),
      },
    );
  };

  const counts = audience.data ?? null;
  const total = counts?.total ?? null;
  const countLine = audience.isLoading
    ? "Counting who this goes to…"
    : audience.error || !counts
      ? "Couldn't count who this would go to. Refresh the page before sending."
      : `Goes to ${people(counts.total)}: ${people(counts.aus)} get the Australian version and ${people(counts.nz)} the Christchurch one.`;

  return (
    <>
      <p className={BODY_TEXT}>
        Thanks everyone who registered for any event of the September visit, in
        Melbourne, Canberra, Sydney or Christchurch, whether they came or not,
        and invites them to learn the meditation. Christchurch registrants get
        their own version that speaks only of their evening. The test sends you
        both versions.
      </p>
      <p className="mt-2 text-[15px] text-[color:var(--color-ink)]">
        {countLine}
      </p>

      <TestThenSend
        flow={flow}
        emailName={
          emailTypeLabel("visit_thank_you") ??
          "Thank you for the September visit"
        }
        audience={total !== null && total > 0 ? people(total) : null}
        noAudienceText={
          audience.isLoading
            ? "Counting who this goes to…"
            : audience.error
              ? "Couldn't count who this would go to. Refresh the page before sending."
              : "There's no one to send this to yet."
        }
        multipleAddresses
        busy={thankYou.isPending}
        onSendTest={(addresses) => send("test", addresses)}
        onSendAll={() => send("all", [])}
      />
    </>
  );
}
